import moment from "moment";
import { isReleaseDraft, isReleaseOnSale, isReleaseScheduled } from "utils/helpers";
import Button from "components/atoms/buttons/Button";
import Modal from "components/atoms/modal";
import { ClipboardDocumentIcon, DocumentTextIcon, QrCodeIcon } from "@heroicons/react/24/outline";
import { copyToClipboard, downloadQRCode } from "utils/HelperFunctions";
import { DATE_FORMAT } from "../../../../../constants";

function PrivateReleaseSection({ eventURL }: { eventURL: string }) {
  const copyHandler = () => copyToClipboard(eventURL);
  const downloadQRCodeHandler = () => downloadQRCode(eventURL);

  return (
    <div className="mt-5 flex">
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="font-sora text-base font-medium opacity-50 md:text-lg">Private Release Details</span>
          <hr className="bg flex-1 opacity-50" />
        </div>
        <div className=" items-center justify-between gap-3">
          <div className="flex justify-between gap-2">
            {/* TODO: @ajay to fix this */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="font-sora text-[0.825rem] font-medium opacity-50">Private Page Link:</label>
            {/* Internal Link */}
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a
              className="flex-1 break-all text-xxs font-medium leading-5 text-white underline underline-offset-1"
              href={eventURL}
              target="_blank"
            >
              {eventURL}
            </a>
          </div>
          <div className="m-2 flex justify-between">
            {/* TODO: @ajay to fix this */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="invisible w-1/5 font-sora text-base font-medium opacity-50">Link Actions:</label>
            <span className="flex flex-1 gap-3">
              <button
                className="flex shrink-0 items-center gap-1 rounded-full bg-black px-3.5 py-2 text-white hover:bg-white hover:text-black hover:opacity-70"
                onClick={copyHandler}
                type="button"
              >
                <ClipboardDocumentIcon title="Copy private page link" className="h-5 w-5" />
                <span className="text-base font-medium">Copy URL</span>
              </button>
              <button
                className="flex shrink-0 items-center gap-1 rounded-full bg-black px-3.5 py-2 text-white hover:bg-white hover:text-black hover:opacity-70"
                onClick={downloadQRCodeHandler}
                type="button"
              >
                <QrCodeIcon title="Download QR Code" className="h-5 w-5" />
                <span className="text-base font-medium">Download QR Code</span>
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface IProps {
  magnetMaxSupply: number;
  open: boolean;
  onClose: () => void;
  release: Release;
  onEditReleaseButtonClick: (id: number) => void;
  handleDeleteRelease: (releaseId: number) => void;
  handlePublishRelease: (releaseId: number) => void;
  setOpenEndReleaseModal: (openEndReleaseModal: boolean) => void;
  setIsManageMagnetReleaseModalOpen: (b: boolean) => void;
}
function ManageReleaseModal({
  magnetMaxSupply,
  open: isModalOpen = false,
  onClose,
  onEditReleaseButtonClick,
  release,
  setOpenEndReleaseModal,
  handleDeleteRelease,
  handlePublishRelease,
  setIsManageMagnetReleaseModalOpen,
}: IProps) {
  let eventURL;
  if ("event_url" in release.event) {
    eventURL = release.event.event_url;
  }
  const renderModelActionButtons = () => (
    <>
      {/* Allow ending a release if the sale is ongoing or scheduled. */}
      {isReleaseScheduled(release.status) || isReleaseOnSale(release.status) ? (
        <Button
          variant="magnetiqSolid"
          text="END RELEASE"
          additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
          onClick={() => {
            setOpenEndReleaseModal(true);
          }}
        />
      ) : null}
      {/* Edit button only visible when release in draft mode. */}
      {isReleaseDraft(release.status) ? (
        <>
          <Button
            variant="whiteOutline"
            text="DELETE"
            additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            onClick={() => {
              handleDeleteRelease(release.id);
              setIsManageMagnetReleaseModalOpen(false);
            }}
          />
          <Button
            variant="rustPinkSolid"
            additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            text="EDIT RELEASE"
            onClick={() => onEditReleaseButtonClick(release.id)}
          />
          <Button
            variant="magnetiqSolid"
            text="PUBLISH RELEASE"
            additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            onClick={() => {
              handlePublishRelease(release.id);
              setIsManageMagnetReleaseModalOpen(false);
            }}
          />
        </>
      ) : null}
    </>
  );

  return (
    <Modal open={isModalOpen} onClose={onClose}>
      <div className="relative rounded-2xl bg-primary pb-6 pl-7 pr-7 pt-6 text-white shadow-modal md:w-[800px]">
        <div className="flex justify-between">
          <section className="w-[95%] font-satoshi text-xl font-bold">Manage Releases</section>
          <span onClick={onClose} className="cursor-pointer">
            X
          </span>
        </div>
        <div className="mt-8 flex w-full flex-col">
          <div className="flex">
            <div className="flex flex-1 flex-col gap-2">
              <span className="font-sora text-base font-medium opacity-50 md:text-lg">Start Date/Time</span>
              <div>
                <span className="font-satoshi text-xl font-bold text-white md:text-3xl">
                  {/* This get automatically converted to local time. */}
                  {moment(release.start_time).format(DATE_FORMAT)}
                </span>
                <span className="font-satoshi text-lg font-bold text-white text-opacity-70 md:text-lg">
                  {moment(release.start_time).format(" hh:mm a")}
                </span>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <span className="font-sora text-base font-medium opacity-50 md:text-lg">End Date/Time</span>
              <div>
                <span className="font-satoshi text-xl font-bold text-white md:text-3xl">
                  {moment(release.end_time).format(DATE_FORMAT)}
                </span>
                <span className="font-satoshi text-lg font-bold text-white text-opacity-70 md:text-lg">
                  {moment(release.end_time).format(" hh:mm a")}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-5 flex">
            <div className="flex flex-1 flex-col gap-2">
              <span className="font-sora text-base font-medium opacity-50 md:text-lg">Supply for Release</span>
              <span className="font-satoshi text-xl font-bold text-white md:text-3xl">
                {release.max_supply}/<span className="text-lg">{magnetMaxSupply}</span>
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <span className="font-sora text-base font-medium opacity-50 md:text-lg">Sale Price</span>
              <span className="font-satoshi text-xl font-bold text-white md:text-3xl">${release.price}</span>
            </div>
          </div>
        </div>
        <div className="mt-5 flex">
          <div className="flex flex-1 flex-col">
            <span className="font-sora text-base font-medium opacity-50 md:text-lg">Total Sold</span>
            <span className="font-satoshi text-3xl  font-bold md:text-5xl">
              {release.sold}/<span className="text-xl text-white  md:text-3xl">{release.max_supply}</span>
            </span>
          </div>
          {release.whitelisted_file_url && (
            <div className="flex flex-1 flex-col">
              <span className="font-sora text-base font-medium opacity-50 md:text-lg">Whitelisted Emails</span>
              <span className="flex w-[90%]  items-center gap-1 font-satoshi text-3xl font-bold md:text-5xl">
                <DocumentTextIcon className="h-5 w-5 shrink-0" />
                <a
                  className="text-xxs font-medium leading-5 text-white underline underline-offset-1"
                  href={release.whitelisted_file_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {release.whitelisted_file_url}
                </a>
              </span>
            </div>
          )}
        </div>
        {eventURL && <PrivateReleaseSection eventURL={eventURL} />}
        <div className="mt-10 flex justify-end gap-3">{renderModelActionButtons()}</div>
      </div>
    </Modal>
  );
}

export default ManageReleaseModal;
